// src-lit/components/collection/collection.logic.ts
/**
 * コレクション関連のビジネスロジック（純粋関数）
 */

/**
 * Storefront API から取得した商品データの型
 */
export interface StorefrontProduct {
  id: string;
  title: string;
  handle: string;
  availableForSale: boolean;
  featuredImage: {
    url: string;
    altText: string | null;
  } | null;
  priceRange: {
    minVariantPrice: {
      amount: string;
      currencyCode: string;
    };
  };
  compareAtPriceRange: {
    minVariantPrice: {
      amount: string;
    } | null;
  } | null;
}

/**
 * GraphQL レスポンスの型
 */
export interface CollectionProductsResponse {
  data: {
    collection: {
      title: string;
      products: {
        pageInfo: {
          hasNextPage: boolean;
          endCursor: string | null;
        };
        nodes: StorefrontProduct[];
      };
    } | null;
  };
  errors?: Array<{ message: string }>;
}

/**
 * 商品表示用のデータ型
 */
export interface ProductDisplayData {
  id: string;
  title: string;
  handle: string;
  url: string;
  availableForSale: boolean;
  imageUrl: string | null;
  imageAlt: string;
  price: number;
  compareAtPrice: number | null;
  discountRate: number | null;
}

/**
 * GraphQL クエリを生成する
 * @param handle コレクションハンドル
 * @param first 取得件数
 * @param after カーソル（ページネーション用）
 */
export function buildGraphQLQuery(
  handle: string,
  first: number,
  after: string | null
): string {
  const afterClause = after ? `, after: "${after}"` : '';
  return `
    query CollectionProducts {
      collection(handle: "${handle}") {
        title
        products(first: ${first}${afterClause}, sortKey: BEST_SELLING) {
          pageInfo {
            hasNextPage
            endCursor
          }
          nodes {
            id
            title
            handle
            availableForSale
            featuredImage {
              url
              altText
            }
            priceRange {
              minVariantPrice {
                amount
                currencyCode
              }
            }
            compareAtPriceRange {
              minVariantPrice {
                amount
              }
            }
          }
        }
      }
    }
  `;
}

/**
 * 割引率を計算する
 * @param price 販売価格
 * @param compareAtPrice 元価格
 * @returns 割引率（整数%）。セール中でない場合は null
 */
export function calculateDiscountRate(
  price: number,
  compareAtPrice: number | null
): number | null {
  if (compareAtPrice === null || compareAtPrice <= price) {
    return null;
  }
  return Math.round(((compareAtPrice - price) / compareAtPrice) * 100);
}

/**
 * 単一の商品データを表示用に変換する
 * @param product Storefront API の商品データ
 */
export function transformProduct(product: StorefrontProduct): ProductDisplayData {
  const price = parseFloat(product.priceRange.minVariantPrice.amount);
  const compareAtPrice = product.compareAtPriceRange?.minVariantPrice?.amount
    ? parseFloat(product.compareAtPriceRange.minVariantPrice.amount)
    : null;
  const discountRate = calculateDiscountRate(price, compareAtPrice);

  return {
    id: product.id,
    title: product.title,
    handle: product.handle,
    url: `/products/${product.handle}`,
    availableForSale: product.availableForSale,
    imageUrl: product.featuredImage?.url ?? null,
    imageAlt: product.featuredImage?.altText ?? product.title,
    price,
    compareAtPrice,
    discountRate,
  };
}

/**
 * GraphQL レスポンスを商品表示用データに変換する
 * @param response GraphQL レスポンス
 */
export function parseProductNodes(
  response: CollectionProductsResponse
): ProductDisplayData[] {
  if (!response.data?.collection?.products?.nodes) {
    return [];
  }

  return response.data.collection.products.nodes.map((node) =>
    transformProduct(node)
  );
}

/**
 * 価格をフォーマットする
 * @param amount 金額
 * @param currencyCode 通貨コード（デフォルト: JPY）
 */
export function formatPrice(amount: number, currencyCode = 'JPY'): string {
  if (currencyCode === 'JPY') {
    return `¥${Math.round(amount).toLocaleString()}`;
  }
  return new Intl.NumberFormat('ja-JP', {
    style: 'currency',
    currency: currencyCode,
  }).format(amount);
}

/**
 * ページネーション情報を取得する
 * @param response GraphQL レスポンス
 */
export function getPageInfo(response: CollectionProductsResponse): {
  hasNextPage: boolean;
  endCursor: string | null;
} {
  if (!response.data?.collection?.products?.pageInfo) {
    return { hasNextPage: false, endCursor: null };
  }
  return response.data.collection.products.pageInfo;
}
